import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useHistory } from 'react-router-dom';
import { PageTitle, useFeatures, useSession } from '@cordata/buffet';
import { format } from 'date-fns';

import { UncontrolledTable } from '~/components/common';

import { useAdmin } from '~/utils/admin';

import {
  adminInstance as admin,
  baseInstance as base,
  toastSuccess,
  toastError,
} from '~/utils';

import ChangePasswordForm from '~/components/User/ChangePasswordForm';

export const sendActivationEmail = (userName, orgCode, mobilePhone) => {
  return admin
    .post('/send-user-activation', {
      'user-name-or-email': userName,
    })
    .then(() =>
      toastSuccess('Success! The activation email has been sent.'))
    .catch(e =>
      toastError(e.response.data && e.response.data.message && e.response.data.message.includes('last logged in')) && 'User is already activated');
};

const ActionsButton = ({
  data,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const hasLoggedIn = !!data.attributes.lastLoginDate;
  const { hasAppKickstarter } = useFeatures();
  const { current } = useSession();
  const isViewingSelf = data.id === current.attributes.userUid;

  // Don't show actions when viewing yourself and you're not a kickstarter user
  if (isViewingSelf && !hasAppKickstarter) {
    return null;
  }

  const toggleIsOpen = () =>
    setIsOpen(p => !p);

  const appUser = {
    id: data.id,
    authenticationService: data.attributes.authenticationService,
  };

  const forgotPassword = () => {
    // forgot password until people say "I want a different email template"
    base
      .post('/forgot-password', {
        'user-name-or-email': data.attributes.userName,
      })
      .then(() => toastSuccess('Password reset!'));
  };

  const orgCode = localStorage.getItem('org')?.slice(-6);

  const deactivateAccount = () => {
    /* eslint-disable-next-line no-restricted-globals */
    admin
      .put(`/app-user/${data.id}/account`, {
        'is-in-selections': false,
        'account-is-locked': true,
        'can-impersonate': false,
        'mfa-required': false,
        'password-reset-required': true,
      })
      .then(() => toastSuccess('Deactivated! Reloading page.', { onClose: () => window.location.reload() }));
  };

  const activateAccount = (skipConfirm = false) => {
    /* eslint-disable-next-line no-restricted-globals */
    admin
      .put(`/app-user/${data.id}/account`, {
        'is-in-selections': true,
        'account-is-locked': false,
        'password-reset-required': true,
        'can-impersonate': false,
        'mfa-required': data.attributes.mfaRequired,
      })
      .then(() => { if (!skipConfirm) toastSuccess('Activated! Reloading page.', { onClose: () => window.location.reload() }); });
  };

  const doActivate = () => {
    // calling activateAccount directly from the onClick passes in an event
    //   for the parameter `skipConfirm`, which is truthy. This avoids that issue.
    activateAccount(false);
  };

  const sendActivation = () => {
    /* eslint-disable-next-line no-restricted-globals */
    activateAccount(true);
    sendActivationEmail(data.attributes.userName, orgCode, data.attributes.mobilePhone);
  };

  return (
    <>
      { appUser.authenticationService === 'cordata' ? (
        <>
          <ButtonDropdown
            size="sm"
            className="w-100"
            isOpen={isDropdownOpen}
            toggle={() => setIsDropdownOpen(wasOpen => !wasOpen)}
          >
            <Button
              outline
              onClick={hasLoggedIn && data.attributes.isInSelections
                ? forgotPassword : sendActivation}
              className="ml-2"
            >
              <FontAwesomeIcon icon={hasLoggedIn && data.attributes.isInSelections ? 'unlock' : 'key'} />

              <span className="d-none d-md-inline ml-2">
                {hasLoggedIn && data.attributes.isInSelections ? 'Reset Password' : 'Send Activation'}
              </span>
            </Button>

            <DropdownToggle outline caret />

            <DropdownMenu right positionFixed>
              <DropdownItem
                onClick={hasLoggedIn && data.attributes.isInSelections
                  ? sendActivation : forgotPassword}
              >
                {hasLoggedIn && data.attributes.isInSelections
                ? 'Send Activation' : 'Reset Password'}
              </DropdownItem>

              {hasAppKickstarter && (
                <DropdownItem onClick={toggleIsOpen} >
                  Change Password
                </DropdownItem>
              )}

              <DropdownItem
                onClick={data.attributes.isInSelections ? deactivateAccount : doActivate}
              >
                {data.attributes.isInSelections ? 'Deactivate Account' : 'Activate Account'}
              </DropdownItem>
            </DropdownMenu>
          </ButtonDropdown>

          <ChangePasswordForm
            appUser={appUser}
            isOpen={isOpen}
            toggle={toggleIsOpen}
          />
        </>
      ) : (
        <Button
          size="sm"
          className="w-100"
          outline
          onClick={data.attributes.isInSelections ? deactivateAccount : doActivate}
        >
          {data.attributes.isInSelections ? 'Deactivate Account' : 'Activate Account'}
        </Button>
      )}
    </>
  );
};

ActionsButton.propTypes = {
  data: PropTypes.shape().isRequired,
};

const defaultSorted = [{
  id: 'lastName',
}, {
  id: 'firstName',
}, {
  id: 'userName',
}];

const columns = [{
  id: 'lastName',
  Header: 'Last Name',
  accessor: 'attributes.lastName',
}, {
  id: 'firstName',
  Header: 'First Name',
  accessor: 'attributes.firstName',
}, {
  id: 'userName',
  Header: 'Username',
  accessor: 'attributes.userName',
}, {
  id: 'emailAddress',
  Header: 'Email Address',
  accessor: 'attributes.emailAddress',
}, {
  id: 'lastLoginDate',
  Header: 'Last Login Date',
  accessor: 'attributes.lastLoginDate',
  Cell: c => (c.value ? format(c.value, 'ddd MMM Do YYYY [@] h:mm A') : 'N/A'),
}, {
  id: 'accountIsLocked',
  Header: 'Locked',
  accessor: 'attributes.accountIsLocked',
  Cell: c => c.value && <div className="text-danger text-center"><FontAwesomeIcon icon="lock" /></div>,
  minWidth: 25,
  filterable: false,
}, {
  id: 'isInSelections',
  Header: 'Active',
  accessor: 'attributes.isInSelections',
  Cell: c => c.value && <div className="text-success text-center"><FontAwesomeIcon icon="check" /></div>,
  minWidth: 25,
  filterable: false,
}, {
  id: 'actions',
  Header: 'Actions',
  filterable: false,
  Cell: c => <ActionsButton data={c.original} />,
}];

const IndexUsers = () => {
  const history = useHistory();
  const { org, div } = useAdmin();
  const { hasAppKickstarter } = useFeatures();

  if (!org) return null;

  return (
    <>
      <PageTitle
        action={org && (
          <Button
            outline
            size="sm"
            tag={Link}
            to="/admin/users/add"
          >
            <FontAwesomeIcon icon="plus" />
            <span className="d-none d-md-inline ml-2">Add User</span>
          </Button>
        )}
      >
        Users
      </PageTitle>

      <UncontrolledTable
        name="users"
        defaultSorted={defaultSorted}
        endpoint={{
          url: 'admin/app-user',
          params: {
            'org-uid': org.id,
            canImpersonate__distinct_from: true,
            isCordata: false,
            ...(div ? {
              divisionUids__contains: div.id,
            } : {}),
            ...(!hasAppKickstarter ? {
              isClientManaged: true,
            } : {}),
          },
        }}
        columns={columns}
        getTdProps={(_state, rowInfo, column) => ({
          style: {
            cursor: 'pointer',
          },
          onClick: (_e, handleOriginal) => {
            if (handleOriginal && !column.id) {
              handleOriginal();
            } else if (column.id !== 'actions') {
              history.push(`/admin/users/${rowInfo.original.id}`);
            }
          },
        })}
        SubComponent={({ original }) => (
          <div className="p-3">
            Found in Divisions: {(original.attributes.divisionNames || []).sort().join(', ')}
          </div>
        )}
      />
    </>
  );
};

export default IndexUsers;
